// src/components/mdx/client/visualizations/signalNoiseMath.ts

/**
 * Shared math utilities for the Signal vs Noise chart.
 * The functions in this file are pure and easy to test, which helps us guard
 * against regressions that would otherwise only surface in interactive charts.
 */

/**
 * Represents a single data point in the signal vs noise visualization.
 * @typedef {Object} SignalNoiseDataPoint
 * @property {number} x - The x-axis value (typically time or index)
 * @property {number} pureSignal - The original signal without noise
 * @property {number} noisySignal - The signal with added noise
 * @property {number} [movingAverage] - Optional moving average value
 * @property {number} [exponentialSmoothing] - Optional exponentially smoothed value
 * @property {number} [medianFilter] - Optional median filtered value
 */
export interface SignalNoiseDataPoint {
  x: number
  pureSignal: number
  noisySignal: number
  movingAverage?: number
  exponentialSmoothing?: number
  medianFilter?: number
}

/**
 * Calculates moving average for the data series
 * 
 * The moving average is computed using a sliding window approach, where each point's
 * average is calculated from the current point and the previous (windowSize - 1) points.
 * This helps smooth out short-term fluctuations and highlight longer-term trends.
 * 
 * @param {SignalNoiseDataPoint[]} data - Array of data points to process
 * @param {number} windowSize - Size of the moving average window (must be positive)
 * @returns {SignalNoiseDataPoint[]} Array of data points with movingAverage property populated
 */
export const calculateMovingAverage = (
  data: SignalNoiseDataPoint[],
  windowSize: number
): SignalNoiseDataPoint[] => {
  if (data.length === 0) {
    return []
  }

  if (windowSize <= 0) {
    return data.map(point => ({
      ...point,
      movingAverage: point.noisySignal
    }))
  }

  const result: SignalNoiseDataPoint[] = []
  let rollingSum = 0

  for (let index = 0; index < data.length; index++) {
    const current: SignalNoiseDataPoint | undefined = data[index]
    if (!current) continue
    
    rollingSum += current.noisySignal

    if (index >= windowSize) {
      const valueToRemove: number = data[index - windowSize]?.noisySignal ?? 0
      rollingSum -= valueToRemove
    }

    const divisor: number = index >= windowSize ? windowSize : index + 1
    const movingAverage: number = divisor > 0 ? rollingSum / divisor : 0

    result[index] = {
      x: current.x,
      pureSignal: current.pureSignal,
      noisySignal: current.noisySignal,
      movingAverage,
      exponentialSmoothing: current.exponentialSmoothing,
      medianFilter: current.medianFilter
    }
  }

  return result
}

/**
 * Applies exponential smoothing to the data
 * 
 * Exponential smoothing gives more weight to recent observations while still considering
 * historical data. The smoothing formula is: St = α * Yt + (1-α) * St-1, where:
 * - St is the smoothed value at time t
 * - Yt is the actual observation at time t
 * - α (alpha) is the smoothing factor between 0 and 1
 * 
 * Higher alpha values give more weight to recent observations, while lower values
 * produce smoother results that change more slowly.
 * 
 * @param {SignalNoiseDataPoint[]} data - Array of data points to process
 * @param {number} alpha - Smoothing factor (0 < alpha <= 1), higher values give more weight to recent observations
 * @returns {SignalNoiseDataPoint[]} Array of data points with exponentialSmoothing property populated
 */
export const applyExponentialSmoothing = (
  data: SignalNoiseDataPoint[],
  alpha: number
): SignalNoiseDataPoint[] => {
  if (data.length === 0) {
    return []
  }

  const result: SignalNoiseDataPoint[] = []
  
  // Initialize with first observation
  const firstPoint: SignalNoiseDataPoint | undefined = data[0]
  if (firstPoint) {
    result[0] = {
      x: firstPoint.x,
      pureSignal: firstPoint.pureSignal,
      noisySignal: firstPoint.noisySignal,
      movingAverage: firstPoint.movingAverage,
      exponentialSmoothing: firstPoint.noisySignal,
      medianFilter: firstPoint.medianFilter
    }
  }
  
  // Apply exponential smoothing formula: St = α * Yt + (1-α) * St-1
  for (let i = 1; i < data.length; i++) {
    const currentPoint: SignalNoiseDataPoint | undefined = data[i]
    const previousPoint: SignalNoiseDataPoint | undefined = result[i - 1]
    
    if (currentPoint && previousPoint) {
      const previousSmoothed: number = previousPoint.exponentialSmoothing ?? previousPoint.noisySignal
      const smoothed: number = alpha * currentPoint.noisySignal + (1 - alpha) * previousSmoothed
      
      result[i] = {
        x: currentPoint.x,
        pureSignal: currentPoint.pureSignal,
        noisySignal: currentPoint.noisySignal,
        movingAverage: currentPoint.movingAverage,
        exponentialSmoothing: smoothed,
        medianFilter: currentPoint.medianFilter
      }
    }
  }
  
  return result
}

/**
 * Performs binary search to find the insertion point for a value in a sorted array.
 * Returns the index where the value should be inserted to maintain sorted order.
 * 
 * @param {number[]} sorted - Sorted array to search
 * @param {number} value - Value to find insertion point for
 * @returns {number} Index where value should be inserted
 */
const lowerBound = (sorted: number[], value: number): number => {
  let low = 0
  let high = sorted.length

  while (low < high) {
    const mid: number = Math.floor((low + high) / 2)
    if ((sorted[mid] ?? 0) < value) {
      low = mid + 1
    } else {
      high = mid
    }
  }

  return low
}

/**
 * Applies median filtering to the data.
 * 
 * Median filtering is a non-linear technique that replaces each data point with the
 * median of neighboring points within a window. This is particularly effective at
 * removing outliers and spike noise while preserving edges better than averaging methods.
 * 
 * The window is centered on each point, extending halfWindow points in each direction
 * (adjusted at the boundaries of the data series).
 * 
 * Uses an optimized sliding window approach with a sorted array to efficiently compute
 * medians without re-sorting the entire window for each point.
 * 
 * @param {SignalNoiseDataPoint[]} data - Array of data points to process
 * @param {number} windowSize - Size of the median filter window (should be odd for symmetric filtering)
 * @returns {SignalNoiseDataPoint[]} Array of data points with medianFilter property populated
 */
export const applyMedianFilter = (
  data: SignalNoiseDataPoint[],
  windowSize: number
): SignalNoiseDataPoint[] => {
  if (data.length === 0) {
    return []
  }

  if (windowSize <= 1) {
    return data.map(point => ({
      ...point,
      medianFilter: point.noisySignal
    }))
  }

  const halfWindow: number = Math.floor(windowSize / 2)
  const result: SignalNoiseDataPoint[] = []
  const sortedWindow: number[] = []

  const addValue = (value: number): void => {
    const index: number = lowerBound(sortedWindow, value)
    sortedWindow.splice(index, 0, value)
  }

  const removeValue = (value: number): void => {
    const index: number = lowerBound(sortedWindow, value)
    if (index < sortedWindow.length && sortedWindow[index] === value) {
      sortedWindow.splice(index, 1)
    }
  }

  const pushInitialWindow = (endIndex: number): void => {
    for (let idx = 0; idx <= endIndex; idx++) {
      const value: number | undefined = data[idx]?.noisySignal
      if (value !== undefined) {
        addValue(value)
      }
    }
  }

  const initialEnd: number = Math.min(data.length - 1, halfWindow)
  pushInitialWindow(initialEnd)

  for (let index = 0; index < data.length; index++) {
    const currentPoint: SignalNoiseDataPoint | undefined = data[index]
    if (!currentPoint) continue

    const windowLength: number = sortedWindow.length
    const mid: number = Math.floor(windowLength / 2)
    let median = 0

    if (windowLength % 2 === 0) {
      const left: number = sortedWindow[mid - 1] ?? 0
      const right: number = sortedWindow[mid] ?? 0
      median = (left + right) / 2
    } else {
      median = sortedWindow[mid] ?? 0
    }

    result[index] = {
      x: currentPoint.x,
      pureSignal: currentPoint.pureSignal,
      noisySignal: currentPoint.noisySignal,
      movingAverage: currentPoint.movingAverage,
      exponentialSmoothing: currentPoint.exponentialSmoothing,
      medianFilter: median
    }

    const currentStart: number = Math.max(0, index - halfWindow)
    const nextStart: number = Math.max(0, index + 1 - halfWindow)
    if (nextStart > currentStart) {
      const valueToRemove: number | undefined = data[currentStart]?.noisySignal
      if (valueToRemove !== undefined) {
        removeValue(valueToRemove)
      }
    }

    const currentEnd: number = Math.min(data.length - 1, index + halfWindow)
    const nextEnd: number = Math.min(data.length - 1, index + 1 + halfWindow)
    if (nextEnd > currentEnd) {
      const valueToAdd: number | undefined = data[nextEnd]?.noisySignal
      if (valueToAdd !== undefined) {
        addValue(valueToAdd)
      }
    }
  }

  return result
}